import { Currency, currencyEquals, ETHER } from '@uniswap/sdk'
import { useMemo } from 'react'
import i18n from '../i18n'
import { tryParseAmount } from '../state/swap/hooks'
import { useTransactionAdder } from '../state/transactions/hooks'
import { useCurrencyBalance } from '../state/wallet/hooks'
import { useActiveWeb3React } from './index'
import { useWETHContract } from './useContract'
import { WETH_EXTENDED } from '../constants'
import { getNativeCurrencySymbol } from '../constants/chains'

export enum WrapType {
  NOT_APPLICABLE,
  WRAP,
  UNWRAP
}

const NOT_APPLICABLE = { wrapType: WrapType.NOT_APPLICABLE }
/**
 * Given the selected input and output currency, return a wrap callback
 * @param inputCurrency the selected input currency
 * @param outputCurrency the selected output currency
 * @param typedValue the user input value
 */
export default function useWrapCallback(
  inputCurrency: Currency | undefined,
  outputCurrency: Currency | undefined,
  typedValue: string | undefined
): { wrapType: WrapType; execute?: undefined | (() => Promise<void>); inputError?: string } {
  const { chainId, account } = useActiveWeb3React()
  const wethContract = useWETHContract()
  const balance = useCurrencyBalance(account ?? undefined, inputCurrency)
  // we can always parse the amount typed as the input currency, since wrapping is 1:1
  const inputAmount = useMemo(() => tryParseAmount(typedValue, inputCurrency), [inputCurrency, typedValue])
  const addTransaction = useTransactionAdder()

  return useMemo(() => {
    if (!wethContract || !chainId || !inputCurrency || !outputCurrency) return NOT_APPLICABLE

    const sufficientBalance = inputAmount && balance && !balance.lessThan(inputAmount)

    const nativeSymbol = getNativeCurrencySymbol(chainId)
    const wrappedSymbol = `W${nativeSymbol}`

    if (inputCurrency === ETHER && WETH_EXTENDED[chainId] && currencyEquals(WETH_EXTENDED[chainId]!, outputCurrency)) {
      return {
        wrapType: WrapType.WRAP,
        execute:
          sufficientBalance && inputAmount
            ? async () => {
                try {
                  const txReceipt = await wethContract.deposit({ value: `0x${inputAmount.raw.toString(16)}` })
                  addTransaction(txReceipt, {
                    summary: i18n.t('wrapSummary', {
                      amount: inputAmount.toSignificant(6),
                      inputSymbol: nativeSymbol,
                      outputSymbol: wrappedSymbol
                    }) as string
                  })
                } catch (error) {
                  console.error('Could not deposit', error)
                }
              }
            : undefined,
        inputError: sufficientBalance ? undefined : (i18n.t('insufficientBalance', { symbol: nativeSymbol }) as string)
      }
    } else if (
      WETH_EXTENDED[chainId] &&
      currencyEquals(WETH_EXTENDED[chainId]!, inputCurrency) &&
      outputCurrency === ETHER
    ) {
      return {
        wrapType: WrapType.UNWRAP,
        execute:
          sufficientBalance && inputAmount
            ? async () => {
                try {
                  const txReceipt = await wethContract.withdraw(`0x${inputAmount.raw.toString(16)}`)
                  addTransaction(txReceipt, {
                    summary: i18n.t('unwrapSummary', {
                      amount: inputAmount.toSignificant(6),
                      inputSymbol: wrappedSymbol,
                      outputSymbol: nativeSymbol
                    }) as string
                  })
                } catch (error) {
                  console.error('Could not withdraw', error)
                }
              }
            : undefined,
        inputError: sufficientBalance ? undefined : (i18n.t('insufficientBalance', { symbol: wrappedSymbol }) as string)
      }
    } else {
      return NOT_APPLICABLE
    }
  }, [wethContract, chainId, inputCurrency, outputCurrency, inputAmount, balance, addTransaction])
}
